<template>
  <div class="empresa-form">
    <!-- Card 1 - Informações do Registro -->
    <b-card class="mb-3 shadow-sm" header-tag="header">
      <template #header>
        <div class="card-header-line">
          <span class="card-title">Informações do registro</span>
          <small class="card-subtitle">Datas de criação e atualização</small>
        </div>
      </template>

      <b-row>
        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="Data de cadastro" label-for="input-data-cadastro" label-class="form-label-sm">
            <b-form-datepicker id="input-data-cadastro" v-model="empresa.data_cadastro" class="w-100"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" locale="pt-br">
              <template #button-content>
                <b-icon icon="calendar" variant="primary" />
              </template>
            </b-form-datepicker>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="Data de atualização" label-for="input-data-atualizacao" label-class="form-label-sm">
            <b-form-datepicker id="input-data-atualizacao" v-model="empresa.data_atualizacao" class="w-100"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" locale="pt-br">
              <template #button-content>
                <b-icon icon="calendar" variant="primary" />
              </template>
            </b-form-datepicker>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Card 2 - Dados Básicos -->
    <b-card class="mb-3 shadow-sm" header-tag="header">
      <template #header>
        <div class="card-header-line">
          <span class="card-title">Dados básicos</span>
          <small class="card-subtitle">Informações principais da empresa</small>
        </div>
      </template>

      <b-row>
        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="CNPJ" label-for="input-cnpj" label-class="form-label-sm">
            <b-input-group>
              <b-form-input id="input-cnpj" v-model="empresa.cnpj" v-mask="['##.###.###/####-##']"
                placeholder="00.000.000/0000-00" required />
              <b-input-group-append>
                <b-button variant="outline-primary" @click="buscarDadosCnpj">
                  Buscar CNPJ
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small class="text-muted">
              Use o botão para preencher automaticamente os dados da empresa.
            </small>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="Razão social" label-for="input-razao" label-class="form-label-sm">
            <b-form-input id="input-razao" v-model="empresa.nome" placeholder="Razão social" required />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="Telefone" label-for="input-telefone" label-class="form-label-sm">
            <b-input-group>
              <b-input-group-prepend is-text>
                <span class="text-muted">+55</span>
              </b-input-group-prepend>
              <b-form-input id="input-telefone" v-model="empresa.telefone" placeholder="(00) 00000-0000" required />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="E-mail" label-for="input-email" label-class="form-label-sm">
            <b-form-input id="input-email" v-model="empresa.email" type="email" placeholder="contato@empresa.com.br"
              required />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Card 3 - Endereço -->
    <b-card class="mb-3 shadow-sm" header-tag="header">
      <template #header>
        <div class="card-header-line">
          <span class="card-title">Endereço da empresa</span>
          <small class="card-subtitle">Dados de localização</small>
        </div>
      </template>

      <b-row>
        <b-col cols="12" md="3" class="mb-3">
          <b-form-group label="CEP" label-for="input-cep" label-class="form-label-sm">
            <b-form-input id="input-cep" v-model="empresa.cep" v-mask="['#####-###']" placeholder="00000-000"
              required />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="Endereço" label-for="input-endereco" label-class="form-label-sm">
            <b-form-input id="input-endereco" v-model="empresa.endereco" placeholder="Rua, número, complemento"
              required />
          </b-form-group>
        </b-col>

        <!-- Bairro -->
        <b-col cols="12" md="3" class="mb-3">
          <b-form-group label="Bairro" label-for="input-bairro" label-class="form-label-sm">
            <b-form-input id="input-bairro" v-model="empresa.bairro" placeholder="Bairro" required />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4" class="mb-3">
          <b-form-group label="Estado" label-class="form-label-sm">
            <b-form-select v-model="estadoSelecionado" class="form-control" @change="onEstadoChange">
              <option :value="''" disabled>Selecione o estado</option>
              <option v-for="estado of estados" :key="estado.id_estado"
                :value="{ uf: estado.sigla, id: estado.id_estado }">
                {{ estado.nome }}
              </option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="8" class="mb-3">
          <b-form-group label="Cidade" label-class="form-label-sm">
            <b-form-select v-model="cidadeSelecionada" class="form-control">
              <option :value="''" disabled>Selecione a cidade</option>
              <option v-for="cidade of cidades" :key="cidade.ibge" :value="{
                idMunicipio: cidade.id_municipio,
                idEstado: cidade.id_estado,
                ibge: cidade.ibge,
                nome: cidade.nome
              }">
                {{ cidade.nome }}
              </option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Card 4 - Dados de Acesso -->
    <b-card class="mb-3 shadow-sm" header-tag="header">
      <template #header>
        <div class="card-header-line">
          <span class="card-title">Dados de acesso</span>
          <small class="card-subtitle">Credenciais da empresa</small>
        </div>
      </template>

      <b-row>
        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="Login" label-for="input-login" label-class="form-label-sm">
            <b-form-input id="input-login" v-model="empresa.login" placeholder="Login da empresa" required />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" class="mb-3">
          <b-form-group label="Senha" label-for="input-senha" label-class="form-label-sm">
            <b-form-input id="input-senha" v-model="empresa.senha" type="password" placeholder="Senha" required />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Card 5 - Configurações -->
    <b-card class="mb-4 shadow-sm" header-tag="header">
      <template #header>
        <div class="card-header-line">
          <span class="card-title">Configurações e cobrança</span>
          <small class="card-subtitle">Opções de uso e faturamento</small>
        </div>
      </template>

      <b-row>
        <!-- Switches -->
        <b-col cols="12" md="6" class="mb-2">
          <b-form-checkbox v-model="empresa.is_digital_signature">
            Habilitar assinatura digital
          </b-form-checkbox>
        </b-col>

        <b-col cols="12" md="6" class="mb-2">
          <b-form-checkbox v-model="empresa.is_api">
            Habilitar API
          </b-form-checkbox>
        </b-col>

        <b-col cols="12" md="6" class="mb-2">
          <b-form-checkbox v-model="empresa.nf">
            Emitir nota fiscal
          </b-form-checkbox>
        </b-col>

        <b-col cols="12" md="6" class="mb-3">
          <b-form-checkbox v-model="empresa.is_test">
            Empresa para teste
          </b-form-checkbox>
        </b-col>

        <b-col cols="12">
          <hr class="section-divider" />
        </b-col>

        <!-- Tipo de contrato e cobrança -->
        <b-col cols="12" md="4" class="mb-3">
          <b-form-group label="Tipo de contrato" label-for="select-tipo-contrato" label-class="form-label-sm">
            <b-form-select id="select-tipo-contrato" v-model="empresa.tipo_contrato" :options="[
              { value: 'mensal', text: 'Mensal' },
              { value: 'anual', text: 'Anual' },
              { value: 'creditos', text: 'Créditos' }
            ]" required />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4" class="mb-3">
          <b-form-group label="Dia da cobrança" label-for="input-dia-cobranca" label-class="form-label-sm">
            <b-form-input id="input-dia-cobranca" v-model.number="empresa.dia_cobranca" type="number" min="1" max="31"
              placeholder="1 a 31" />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4" class="mb-3">
          <b-form-group label="Valor da cobrança (R$)" label-for="input-valor-cobranca" label-class="form-label-sm">
            <b-input-group>
              <b-input-group-prepend is-text>R$</b-input-group-prepend>
              <b-form-input id="input-valor-cobranca" v-model.number="empresa.valor_cobranca" type="number" step="0.01"
                min="0" placeholder="Ex: 199,90" />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col cols="12" class="mb-2">
          <b-form-group label="Nome do responsável" label-for="input-responsavel" label-class="form-label-sm">
            <b-form-input id="input-responsavel" v-model="empresa.responsavel" placeholder="Nome do responsável"
              required />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <div class="text-right">
      <b-button type="submit" variant="success" @click.prevent="salvarEmpresa">
        Salvar
      </b-button>
    </div>

    <loading :active.sync="isLoading" color="#008e75" :can-cancel="true" />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import estadoService from '@src/services/estado'
import empresaService from '@src/services/empresa_sistema'
import cnpjService from '@src/services/cnpj'
import { mask } from 'vue-the-mask'

export default {
  directives: { mask },
  components: { Loading },
  props: {
    empresa: {},
  },

  data() {
    return {
      estadoEmpresa: '',
      cidadeEmpresa: '',
      estadoSelecionado: '',
      cidadeSelecionada: '',
      cidades: [],
      estados: [],
      isLoading: true,
      modal: {
        mensagems: [],
        show: false,
      },
    }
  },

  watch: {
    // Só cidade precisa atualizar o modelo da empresa
    cidadeSelecionada(newValue) {
      if (newValue && newValue.idMunicipio) {
        this.empresa.id_municipio = newValue.idMunicipio
      }
    },
  },

  mounted() {
    this.carregarEstadosEPreSelecionar()
  },

  methods: {
    loading(a) {
      a = !this.isLoading ? true : false
      return a
    },

    async carregarEstadosEPreSelecionar() {
      const token = this.$store.state.auth.currentUser.token

      try {
        const resposta = await estadoService.lista(token)
        this.estados = resposta.data.estados
        this.isLoading = this.loading(this.isLoading)

        // Se estiver editando (já tem id_estado), pré-seleciona estado e carrega cidades
        if (this.empresa.id_estado) {
          const estado = this.estados.find(
            (e) => e.id_estado === this.empresa.id_estado,
          )

          if (estado) {
            this.estadoSelecionado = {
              uf: estado.sigla,
              id: estado.id_estado,
            }
            await this.listandoCidade(estado.id_estado)
          }
        }
      } catch (e) {
        this.isLoading = this.loading(this.isLoading)
      }
    },

    async listandoCidade(id) {
      if (!id) return
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = this.loading(this.isLoading)

      const resposta = await estadoService.listaCidades(token, id)
      this.cidades = resposta.data.cidades
      this.isLoading = this.loading(this.isLoading)

      // Se já existe id_municipio (edição), pré-seleciona cidade
      if (this.empresa.id_municipio) {
        const cidade = this.cidades.find(
          (c) => c.id_municipio === this.empresa.id_municipio,
        )
        if (cidade) {
          this.cidadeSelecionada = {
            idMunicipio: cidade.id_municipio,
            idEstado: cidade.id_estado,
            ibge: cidade.ibge,
            nome: cidade.nome,
          }
        }
      }
    },

    onEstadoChange(estado) {
      // chamada só quando o usuário troca manualmente
      if (estado && estado.id) {
        this.empresa.id_estado = estado.id
        this.empresa.id_municipio = null
        this.cidadeSelecionada = ''
        this.listandoCidade(estado.id)
      }
    },

    limparFormularioEmpresa() {
      this.empresa = {
        nome: '',
        cnpj: '',
        telefone: '',
        email: '',
        registro_siga: '',
        login: '',
        senha: '',
        cep: '',
        endereco: '',
        bairro: '',
        id_estado: null,
        id_municipio: null,
        dia_cobranca: null,
        valor_cobranca: null,
        tipo_contrato: 'mensal',
        responsavel: '',
      }
      this.estadoSelecionado = ''
      this.cidadeSelecionada = ''
      this.cidades = []
    },

    async buscarDadosCnpj() {
      const cnpjLimpo = (this.empresa.cnpj || '').replace(/\D/g, '')

      if (cnpjLimpo.length !== 14) {
        this.$bvToast.toast('Informe um CNPJ válido com 14 dígitos.', {
          variant: 'warning',
          solid: true,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }

      const token = this.$store.state.auth.currentUser.token

      const normalizeText = (text) => {
        return String(text || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim()
      }

      try {
        this.isLoading = true

        const { data } = await cnpjService.consultarCnpj(token, cnpjLimpo)

        if (!data.ok) {
          this.$bvToast.toast(
            data.message || 'Não foi possível consultar o CNPJ.',
            {
              variant: 'danger',
              solid: true,
              noCloseButton: true,
              toaster: 'b-toaster-top-center',
            },
          )
          return
        }

        this.empresa.nome = data.nome || this.empresa.nome
        this.empresa.email = data.email || this.empresa.email
        this.empresa.telefone = data.telefone || this.empresa.telefone
        this.empresa.cep = data.cep || this.empresa.cep

        this.empresa.endereco = [data.logradouro, data.numero, data.complemento]
          .filter((v) => v != null && String(v).trim() !== '')
          .join(', ')

        this.empresa.bairro = data.bairro || this.empresa.bairro

        const estado = this.estados.find((e) => e.sigla === data.uf)
        if (estado) {
          this.empresa.id_estado = estado.id_estado
          this.estadoSelecionado = { uf: estado.sigla, id: estado.id_estado }

          await this.listandoCidade(estado.id_estado)

          const municipioApi = normalizeText(data.municipio)

          const cidade = this.cidades.find(
            (c) => normalizeText(c.nome) === municipioApi,
          )

          if (cidade) {
            this.empresa.id_municipio = cidade.id_municipio
            this.cidadeSelecionada = {
              idMunicipio: cidade.id_municipio,
              idEstado: cidade.id_estado,
              ibge: cidade.ibge,
              nome: cidade.nome,
            }
          }
        }

        this.$bvToast.toast('Dados preenchidos a partir do CNPJ.', {
          variant: 'success',
          solid: true,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (e) {
        const msg =
          e.response.data.message ||
          e.message ||
          'Erro ao consultar CNPJ. Tente novamente mais tarde.'

        this.$bvToast.toast(msg, {
          variant: 'danger',
          solid: true,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } finally {
        this.isLoading = false
      }
    },

    salvarEmpresa() {
      this.isLoading = true
      const token = this.$store.state.auth.currentUser.token

      const tratarResposta = (resposta) => {
        this.isLoading = false
        if (resposta.data.sucesso) {
          this.$bvToast.toast(resposta.data.mensagem, {
            variant: 'success',
            solid: true,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          })
          this.limparFormularioEmpresa()
          this.$root.$emit('back')
        } else if (
          resposta.data.statusCode &&
          resposta.data.statusCode !== 200
        ) {
          this.$bvToast.toast(resposta.data.message, {
            variant: 'danger',
            solid: true,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          })
        }
      }

      const tratarErro = (erro) => {
        this.isLoading = false
        const mensagemErro =
          erro.response && erro.response.data && erro.response.data.message
            ? erro.response.data.message
            : 'Erro desconhecido'
        this.$bvToast.toast(mensagemErro, {
          variant: 'danger',
          solid: true,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      }

      delete this.empresa.logo

      const acao = this.empresa.id_empresa_sistema
        ? empresaService.atualizarEmpresa(token, this.empresa)
        : empresaService.salvarEmpresa(token, this.empresa)

      acao.then(tratarResposta).catch(tratarErro)
    },
  },
}
</script>


<style lang="scss">
.empresa-form {
  .card-header-line {
    display: flex;
    flex-direction: column;
  }

  .card-title {
    font-weight: 600;
    font-size: 15px;
  }

  .card-subtitle {
    font-size: 11px;
    color: #6c757d;
  }

  .form-label-sm {
    font-size: 12px;
    font-weight: 500;
  }

  .section-divider {
    margin: 10px 0 15px;
    border-color: #e9ecef;
  }
}
</style>